// Tour progress lives outside React on purpose: every route section wraps its
// own AppLayout, so the layout (and any provider mounted in it) can unmount and
// remount on cross-route navigation. React state in a provider there would reset
// mid-tour; a module-level store survives the remount, and TourProvider
// re-attaches to it via useSyncExternalStore.

export interface TourState {
  activeTourId: string;
  stepIndex: number;
  /** `${tourId}:${stepIndex}` once that step's route navigation has fired, so a
   * remount (or a redirecting route) can never re-trigger navigate in a loop. */
  navigatedFor: string | null;
  /** Indices of `skipIfAbsent` steps dropped during this run. Sticky for the
   * run, so backward navigation across a route boundary (where the step's page
   * isn't rendered and can't be queried) skips them consistently too. */
  skipped: number[];
  /** Detail routes resolved for adaptive steps this run, by step index. Sticky
   * for the run so Back re-enters the same detail page even when the source
   * list page isn't rendered to re-probe. */
  resolvedPaths: Record<number, string>;
  /** Every route this run may render demo data on — the authored route of each
   * demo step, plus any detail path resolved for one. Held for the whole run, not
   * per step: a list and the detail page it links to are both demo, so keeping the
   * set stable across that handoff removes any window where the page being left
   * loses its demo client and its label while still painting fixture rows. */
  demoRoutes: string[];
}

type Listener = () => void;

let state: TourState | null = null;
const listeners = new Set<Listener>();

export function getTourState(): TourState | null {
  return state;
}

export function setTourState(next: TourState | null): void {
  state = next;
  listeners.forEach((listener) => listener());
}

export function subscribeTourState(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
